import React, { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useListApiKeys,
  getListApiKeysQueryKey,
  useCreateAdminAssessment,
  getListAdminAssessmentsQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAdminAuth } from "@/contexts/admin-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Plus, ExternalLink, Copy, Check, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const createSchema = z.object({
  participantId: z
    .string()
    .min(1, "UUID obrigatório")
    .refine((v) => UUID_REGEX.test(v), { message: "Formato UUID inválido" }),
  apiKeyId: z.string().min(1, "Selecione uma chave"),
  companyId: z
    .string()
    .min(1, "Company ID obrigatório")
    .refine((v) => UUID_REGEX.test(v), { message: "Formato UUID inválido" }),
  participantName: z.string().max(255).optional(),
  participantEmail: z.string().min(1, "E-mail obrigatório").email("E-mail inválido"),
  format: z.enum(["discursive", "multiple_choice"]).default("discursive"),
});
type CreateValues = z.infer<typeof createSchema>;

export function NewAssessmentDialog() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { logout } = useAdminAuth();
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState<{ url: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: apiKeys } = useListApiKeys({ query: { queryKey: getListApiKeysQueryKey() } });
  const activeKeys = apiKeys?.filter((k) => k.active) ?? [];

  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { participantId: "", apiKeyId: "", companyId: "", participantName: "", participantEmail: "", format: "discursive" },
  });

  const mutation = useCreateAdminAssessment({
    mutation: {
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: getListAdminAssessmentsQueryKey() });
        setCreated({ url: (data as { url: string }).url });
        form.reset();
      },
      onError: (err) => {
        if ((err as { status?: number }).status === 401) { logout(); return; }
        toast({ title: "Erro ao criar sessão", variant: "destructive" });
      },
    },
  });

  const copy = () => {
    if (created?.url) {
      navigator.clipboard.writeText(created.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSubmit = (d: CreateValues) => {
    mutation.mutate({
      data: {
        participantId: d.participantId,
        apiKeyId: d.apiKeyId,
        companyId: d.companyId,
        participantName: d.participantName || null,
        participantEmail: d.participantEmail,
        format: d.format,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setCreated(null); setCopied(false); } }}>
      <DialogTrigger asChild>
        <Button data-testid="button-new-session">
          <Plus className="h-4 w-4 mr-2" />
          Nova Sessão
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Criar nova sessão de entrevista</DialogTitle>
          <DialogDescription>Informe o UUID do participante e a chave de API. Os dados pessoais são opcionais.</DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="space-y-4 pt-2">
            <Alert className="bg-emerald-50 border-emerald-200">
              <AlertCircle className="h-4 w-4 text-emerald-700" />
              <AlertTitle className="text-emerald-700">Sessão criada!</AlertTitle>
              <AlertDescription className="mt-2 space-y-3">
                <p className="text-sm">Copie o link abaixo e envie ao participante.</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white border px-3 py-2 rounded text-xs font-mono break-all" data-testid="text-session-url">
                    {created.url}
                  </code>
                  <Button variant="secondary" size="sm" onClick={copy} className="shrink-0" data-testid="button-copy-url">
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <a href={created.url} target="_blank" rel="noopener noreferrer" data-testid="button-open-chat">
                      <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                      Abrir chat
                    </a>
                  </Button>
                  <Button size="sm" onClick={() => { setCreated(null); setCopied(false); }}>
                    Criar outra
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 pt-2">
              <FormField control={form.control} name="participantId" render={({ field }) => (
                <FormItem>
                  <FormLabel>UUID do participante</FormLabel>
                  <FormControl>
                    <Input placeholder="ex: candidato-001 ou UUID qualquer" data-testid="input-participant-uuid" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              <FormField control={form.control} name="apiKeyId" render={({ field }) => (
                <FormItem>
                  <FormLabel>Chave de API</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-api-key">
                        <SelectValue placeholder="Selecione uma chave" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {activeKeys.length === 0
                        ? <SelectItem value="__none__" disabled>Nenhuma chave ativa</SelectItem>
                        : activeKeys.map((k) => (
                          <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />

              <FormField control={form.control} name="companyId" render={({ field }) => (
                <FormItem>
                  <FormLabel>Company ID</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      data-testid="input-company-id"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              <FormField control={form.control} name="format" render={({ field }) => (
                <FormItem>
                  <FormLabel>Formato da avaliação</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-format">
                        <SelectValue placeholder="Selecione o formato" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="discursive">Discursiva (chat com IA)</SelectItem>
                      <SelectItem value="multiple_choice">Múltipla Escolha</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />

              <div className="border-t pt-4">
                <p className="text-xs text-muted-foreground mb-3">Dados do participante (opcionais — podem ser preenchidos pelo próprio participante)</p>

                <div className="space-y-3">
                  <FormField control={form.control} name="participantName" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome completo</FormLabel>
                      <FormControl>
                        <Input placeholder="Nome completo" data-testid="input-participant-name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="participantEmail" render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-mail</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="email@exemplo.com" data-testid="input-participant-email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={mutation.isPending} data-testid="button-confirm-create-session">
                  {mutation.isPending ? "Criando..." : "Criar sessão"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
